import React, { useState } from 'react';
import { 
  Email, 
  Phone, 
  WhatsApp, 
  Language, 
  LocationOn,
  LinkedIn,
  Twitter,
  Facebook,
  Instagram,
  GitHub,
  YouTube as YouTubeIcon,
  EmojiEvents,
  PictureAsPdf,
  VideoLibrary,
  YouTube,
  CheckCircle,
  Work,
  Event,
  Business,
  Person,
  Description,
  Image as ImageIcon,
  PlayArrow,
  Download,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Close,
  Remove
} from '@mui/icons-material';
import { SvgIcon, Box, Typography } from '@mui/material';
import styled from 'styled-components';
import { staticBaseUrl } from '../../../config';
import { saveContactToDevice } from '../../../utils/vCardUtils.js';
import { trackCardInteraction } from '../../../api/analyticsApi';

// Add BatchesGrid styled component
const BatchesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 20px;
  justify-items: center;
  align-items: start;
  margin-top: 15px;
  padding: 0 10px;
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
  }
  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 35px;
  }
`;

// Custom X (former Twitter) Icon
const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </SvgIcon>
);

// import curvyImage from '../../../assets/background.png';

// Styled components for achievements
const AchievementContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }
`;

const AchievementImageWrapper = styled.div`
  width: 100%;
  margin-bottom: 12px;
`;

const AchievementCaption = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  text-align: center;
  line-height: 1.3;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin: 0 0 16px 0;
    padding-bottom: 6px;
  }
`;

const AchievementLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const AchievementButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  .achievement-icon {
    font-size: 18px;
  }

  &.certificate {
    color: white;
    background: linear-gradient(135deg, #4CAF50, #45a049);
    &:hover {
      background: linear-gradient(135deg, #45a049, #3d8b40);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(76, 175, 80, 0.3);
    }
  }

  &.pdf {
    color: white;
    background: linear-gradient(135deg, #2196F3, #1976D2);
    &:hover {
      background: linear-gradient(135deg, #1976D2, #1565C0);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(33, 150, 243, 0.3);
    }
  }

  &.video {
    color: white;
    background: linear-gradient(135deg, #FF9800, #F57C00);
    &:hover {
      background: linear-gradient(135deg, #F57C00, #E65100);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(255, 152, 0, 0.3);
    }
  }
`;

const MediaContainer = styled.div`
  margin-bottom: 12px;
  
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  
  @media (max-width: 768px) {
    height: 140px;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }

  .play-icon {
    font-size: 36px;
    color: white;
    opacity: 0.9;
    transition: all 0.3s ease;
    
    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  &:hover .play-icon {
    transform: scale(1.1);
    opacity: 1;
  }
`;

const YouTubeEmbed = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
`;

// First define the basic styled components
const CountrySelect = styled.select`
  padding: 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  font-size: 13px;
  width: 80px;
`;

const PhoneInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  width: 120px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #4285f4;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

// Then define components that depend on them
const FloatingContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 20px;
  z-index: 1000;
  max-width: 600px;
  margin: 0 auto;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(${props => props.minimized ? '100px' : '0'});
  opacity: ${props => props.minimized ? '0' : '1'};
  pointer-events: ${props => props.minimized ? 'none' : 'auto'};
`;

const SaveContactButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #4285f4, #34a853);
  color: white;
  border: none;
  padding: ${props => props.isMinimized ? '8px' : '8px 16px'};
  border-radius: 20px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  transform-origin: left center;
  transform: ${props => props.isMinimized ? 'scale(0.9)' : 'scale(1)'};

  svg {
    font-size: 18px;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.isMinimized ? 'scale(1.2)' : 'scale(1)'};
  }

  span {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    opacity: ${props => props.isMinimized ? '0' : '1'};
    max-width: ${props => props.isMinimized ? '0' : '200px'};
    overflow: hidden;
  }

  &:hover {
    transform: ${props => props.isMinimized ? 'scale(0.95) translateY(-2px)' : 'scale(1.02) translateY(-2px)'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }
`;

const MinimizeContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(${props => props.minimized ? '0' : '100px'});
  opacity: ${props => props.minimized ? '1' : '0'};
  pointer-events: ${props => props.minimized ? 'auto' : 'none'};
`;

const MinimizeButton = styled.button`
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border: none;
  padding: 8px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${props => props.minimized ? 'bounceIn 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'};

  svg {
    font-size: 24px;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px) scale(1.1);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);

    svg {
      transform: translateY(-2px);
    }
  }

  @keyframes bounceIn {
    0% {
      transform: scale(0.3);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const WhatsAppContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${props => props.isExpanded ? 'expandIn 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'};

  @keyframes expandIn {
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const WhatsAppButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  animation: slideIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    font-size: 18px;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: scale(1.02) translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.2);

    svg {
      transform: rotate(-15deg);
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const WhatsAppForm = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 6px;
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: ${props => props.isExpanded ? '460px' : '0'};
  overflow: hidden;
  opacity: ${props => props.isExpanded ? '1' : '0'};
  transform: ${props => props.isExpanded ? 'scale(1)' : 'scale(0.95)'};
  transform-origin: left center;

  ${CountrySelect}, ${PhoneInput} {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.isExpanded ? 'translateX(0)' : 'translateX(-20px)'};
    opacity: ${props => props.isExpanded ? '1' : '0'};
  }

  ${ButtonGroup} {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${props => props.isExpanded ? 'translateX(0)' : 'translateX(20px)'};
    opacity: ${props => props.isExpanded ? '1' : '0'};
  }
`;

const SendButton = styled.button`
  background: linear-gradient(135deg, #25D366, #128C7E);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    font-size: 18px;
    transition: transform 0.3s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.2);

    svg {
      transform: rotate(-15deg);
    }
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const IconButton = styled.button`
  background: ${props => props.variant === 'close' ? '#ff4444' : '#e0e0e0'};
  color: ${props => props.variant === 'close' ? 'white' : '#666'};
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;

  svg {
    font-size: 18px;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: rotate(90deg) scale(1.1);
    background: ${props => props.variant === 'close' ? '#ff3333' : '#d0d0d0'};
  }
`;

const UploadedVideoPlayer = styled.video`
  width: 100%;
  height: 180px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  object-fit: cover;
  
  @media (max-width: 768px) {
    height: 140px;
  }
`;

const AchievementImage = styled.img`
  width: 100%;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  background: #f8f9fa;
  padding: 8px;
  
  @media (max-width: 768px) {
    height: 70px;
    padding: 6px;
  }
`;

const BatchImage = styled.img`
  width: 100%;
  max-width: 95px;
  height: 80px;
  object-fit: contain;
  border-radius: 8px;
  margin: auto;
  display: block;
  padding: 8px;
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    max-width: 85px;
    height: 70px;
    padding: 6px;
  }

  &:hover {
    transform: scale(1.05);
  }
`;

const StartupTheme = ({ card, isPreview, isEditMode }) => {
  const [playingVideos, setPlayingVideos] = useState({});
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+65'); // Default to Singapore
  const [error, setError] = useState('');
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const toggleVideo = (achievementId) => {
    setPlayingVideos(prev => ({
      ...prev,
      [achievementId]: !prev[achievementId]
    }));
  };

  const handleDownloadVCard = async () => {
    try {
      await saveContactToDevice(card);
      await trackCardInteraction(card.id, 'vcard_download');
    } catch (error) {
      console.error('Error saving contact:', error);
    }
  };

  const handleWhatsAppClick = () => {
    setIsFormExpanded(true);
  };

  const handleCloseForm = () => {
    setIsFormExpanded(false);
    setPhoneNumber('');
    setError('');
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    const phoneRegex = /^\d{8,10}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setError('Please enter a valid phone number (8-10 digits)');
      return;
    }
    
    // Clear any previous error
    setError('');
    
    // Prepare contact details message
    const contactDetails = `
Hi! Here are my contact details:
${card.name}
${card.title || ''}
${card.company || ''}
${card.email || ''}
${card.phone || ''}
${card.website || ''}
${card.location || ''}
    `.trim();
    
    // Format the WhatsApp URL
    const whatsappNumber = `${countryCode}${phoneNumber}`;
    const encodedMessage = encodeURIComponent(contactDetails);
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\+/g, '')}?text=${encodedMessage}`;
    
    // Reset form
    setPhoneNumber('');
    setIsFormExpanded(false);
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setTimeout(() => setIsFormExpanded(false), 100);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setIsFormExpanded(false);
  };

  // Helper function to get image URL with comprehensive debugging
  const getImageUrl = (url) => {
    console.log('=== StartupTheme Image URL Processing ===');
    console.log('Input URL:', url);
    console.log('Static Base URL:', staticBaseUrl);
    
    if (!url) {
      console.log('URL is empty, returning empty string');
      return '';
    }
    
    // Handle absolute URLs (http/https/blob)
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
      console.log('URL is absolute, returning as-is:', url);
      return url;
    }
    
    // Handle relative URLs that might start with /static/
    if (url.startsWith('/static/')) {
      const baseUrl = staticBaseUrl.replace('/static', '');
      const fullUrl = `${baseUrl}${url}`;
      console.log('URL starts with /static/, constructed:', fullUrl);
      return fullUrl;
    }
    
    // Handle relative URLs that might start with uploads/
    if (url.startsWith('uploads/')) {
      const fullUrl = `${staticBaseUrl}/${url}`;
      console.log('URL starts with uploads/, constructed:', fullUrl);
      return fullUrl;
    }
    
    // Handle plain filenames
    const fullUrl = `${staticBaseUrl}/uploads/${url}`;
    console.log('Plain filename, constructed:', fullUrl);
    console.log('=== End Image URL Processing ===');
    return fullUrl;
  };

  // Helper function to check if a URL is an image
  const isImageFile = (url) => {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
    const urlLower = url.toLowerCase();
    return imageExtensions.some(ext => urlLower.includes(ext));
  };

  // Helper function to check if a URL is a video file
  const isVideoFile = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext));
  };

  // Helper function to get file extension
  const getFileExtension = (url) => {
    if (!url) return '';
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.pdf')) return 'pdf';
    if (urlLower.includes('.doc') || urlLower.includes('.docx')) return 'doc';
    if (urlLower.includes('.mp4') || urlLower.includes('.avi') || urlLower.includes('.mov') || urlLower.includes('.wmv') || urlLower.includes('.webm')) return 'video';
    if (urlLower.includes('.jpg') || urlLower.includes('.jpeg') || urlLower.includes('.png') || urlLower.includes('.gif') || urlLower.includes('.bmp') || urlLower.includes('.webp')) return 'image';
    return 'unknown';
  };

  // Helper function to extract YouTube video ID and create embed URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return null;
    return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&autoplay=1&rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&autohide=0&showinfo=1`;
  };

  const {
    name = '',
    designation = '',
    company_name = '',
    address = '',
    about = '',
    description = '',
    bio = '',
    profile_picture_url = '',
    company_logo_url = '',
    cover_photo_url = '',
    contacts = [],
    websites = [],
    social_profiles = [],
    contents = [],
    product_images = []
  } = card || {};

  // Helper function to get contact info by type
  const getContactByType = (type) => {
    if (!Array.isArray(contacts)) return null;
    return contacts.find(contact => contact.type === type);
  };

  // Helper function to get website
  const getWebsite = () => {
    if (Array.isArray(websites) && websites.length > 0) {
      return websites[0].url;
    }
    return '';
  };

  // Get contact details
  const emailContact = getContactByType('email');
  const phoneContact = getContactByType('phone');
  const whatsappContact = getContactByType('whatsapp');
  const website = getWebsite();
  
  // Get social media links
  const socialLinks = Array.isArray(social_profiles) ? social_profiles : [];
  
  // Get bio/about text - check multiple possible field names and sources
  let bioText = '';
  
  // Check direct fields first
  if (bio) {
    bioText = bio;
  } else if (description) {
    bioText = description;
  } else if (about) {
    bioText = about;
  } else if (card.user?.about) {
    bioText = card.user.about;
  } else if (Array.isArray(contents) && contents.length > 0) {
    // Find about content in contents array
    const aboutContentItem = contents.find(c => 
      c.fieldName?.toLowerCase() === 'about' || 
      c.fieldName?.toLowerCase() === 'bio' || 
      c.fieldName?.toLowerCase() === 'description' ||
      c.fieldName?.toLowerCase() === 'about me' ||
      c.field_name?.toLowerCase() === 'about' || 
      c.field_name?.toLowerCase() === 'bio' || 
      c.field_name?.toLowerCase() === 'description' ||
      c.field_name?.toLowerCase() === 'about me'
    );
    if (aboutContentItem) {
      bioText = aboutContentItem.fieldValue || aboutContentItem.field_value || '';
    }
  }
  
  // Helper function to strip HTML tags and convert to plain text
  const stripHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/<p>/g, '')
      .replace(/<\/p>/g, '\n')
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  };
  
  // Clean the bioText
  bioText = stripHtml(bioText);
  

  
  // Get company info
  const companyName = company_name || '';
  const jobTitle = designation || '';
  
  // Get images
  const profileImage = profile_picture_url || '';
  const companyLogo = company_logo_url || '';
  const coverPhoto = cover_photo_url || '';
  const galleryImages = Array.isArray(product_images) ? product_images : [];
  
  // Get additional content
  const additionalContent = Array.isArray(contents) ? contents : [];

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Format phone number with country codes
    const cleaned = phone.replace(/\D/g, '');
    
    // Check if it's an Indian number (10 digits starting with 6-9)
    if (cleaned.length === 10 && /^[6-9]/.test(cleaned)) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    
    // Check if it's a Singapore number (8 digits starting with 8 or 9)
    if (cleaned.length === 8 && /^[89]/.test(cleaned)) {
      return `+65 ${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
    }
    
    // For other numbers, try to detect country code
    if (cleaned.length === 12 && cleaned.startsWith('91')) {
      // Indian number with country code
      const number = cleaned.slice(2);
      return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
    }
    
    if (cleaned.length === 11 && cleaned.startsWith('65')) {
      // Singapore number with country code
      const number = cleaned.slice(2);
      return `+65 ${number.slice(0, 4)} ${number.slice(4)}`;
    }
    
    // Default formatting for other numbers
    if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} - ${cleaned.slice(6)}`;
    }
    
    return phone;
  };

  const formatWebsite = (website) => {
    if (!website) return '';
    return website.replace(/^https?:\/\//, '');
  };

  // Social media icon mapping
  const getSocialIcon = (platform) => {
    const platformLower = platform.toLowerCase();
    switch (platformLower) {
      case 'linkedin':
        return <LinkedIn />;
      case 'twitter':
        return <XIcon />;
      case 'facebook':
        return <Facebook />;
      case 'instagram':
        return <Instagram />;
      case 'youtube':
        return <YouTube />;
      case 'github':
        return <GitHub />;
      case 'whatsapp':
        return <WhatsApp />;
      default:
        return <Language />;
    }
  };

  // Get social media background color
  const getSocialColor = (platform) => {
    const platformLower = platform.toLowerCase();
    switch (platformLower) {
      case 'linkedin':
        return 'linear-gradient(135deg, #0077b5, #00a0dc)';
      case 'twitter':
      case 'x':
        return 'linear-gradient(135deg, #000000, #333333)';
      case 'facebook':
        return 'linear-gradient(135deg, #1877f2, #42a5f5)';
      case 'instagram':
        return 'linear-gradient(135deg, #e4405f, #fd1d1d, #fcb045)';
      case 'youtube':
        return 'linear-gradient(135deg, #ff0000, #cc0000)';
      case 'github':
        return 'linear-gradient(135deg, #333, #24292e)';
      case 'whatsapp':
        return 'linear-gradient(135deg, #25d366, #128c7e)';
      default:
        return 'linear-gradient(135deg, #4285f4, #9c27b0)';
    }
  };

  // Only show buttons if not in preview or edit mode
  const shouldShowButtons = !isPreview && !isEditMode;

  return (
    <div className="startup-theme">
      <style>
        {`
          .startup-theme {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            background: #ffffff;
            position: relative;
            overflow-x: hidden;
            max-width: 100%;
            margin: 0 auto;
          }

          @media (min-width: 768px) {
            .startup-theme {
              max-width: 768px;
            }
          }

          .startup-body {
            flex: 1;
            padding-bottom: 100px; /* Space for floating button */
          }

          @media (max-width: 768px) {
            .startup-body {
              padding-bottom: 80px;
            }
          }

          .startup-header {
            position: relative;
            background: ${card.cover_photo_url ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' : '#ffffff'};
            padding: 30px 20px;
            color: ${card.cover_photo_url ? 'white' : '#1a202c'};
            text-align: center;
          }

          @media (max-width: 400px) {
            .startup-header {
              padding: ${card.cover_photo_url ? '30px 20px 60px' : '20px'};
              min-height: ${card.cover_photo_url ? '180px' : 'auto'};
            }
          }

          .cover-photo {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            display: ${card.cover_photo_url ? 'block' : 'none'};
          }

          .cover-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.1);
            display: ${card.cover_photo_url ? 'block' : 'none'};
          }

          .profile-section {
            position: relative;
            margin-bottom: ${card.cover_photo_url ? '20px' : '10px'};
          }

          .work-badge {
            display: inline-block;
            background: ${card.cover_photo_url ? '#6c757d' : '#f0f0f0'};
            color: ${card.cover_photo_url ? 'white' : '#4a5568'};
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: ${card.cover_photo_url ? '20px' : '15px'};
          }

          .profile-image {
            width: 140px;
            height: 140px;
            border-radius: 50%;
            object-fit: cover;
            border: ${card.cover_photo_url ? '5px solid white' : '5px solid #f0f0f0'};
            box-shadow: 0 8px 25px rgba(0, 0, 0, ${card.cover_photo_url ? '0.15' : '0.08'});
            margin-bottom: 25px;
          }

          .profile-placeholder {
            width: 140px;
            height: 140px;
            border-radius: 50%;
            background: ${card.cover_photo_url ? 'linear-gradient(135deg, #4285f4, #34a853)' : '#f0f0f0'};
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${card.cover_photo_url ? 'white' : '#4a5568'};
            font-size: 48px;
            font-weight: bold;
            margin: 0 auto 25px;
            border: ${card.cover_photo_url ? '5px solid white' : '5px solid #f0f0f0'};
            box-shadow: 0 8px 25px rgba(0, 0, 0, ${card.cover_photo_url ? '0.15' : '0.08'});
          }

          @media (max-width: 400px) {
            .profile-image,
            .profile-placeholder {
              width: 120px;
              height: 120px;
              border: ${card.cover_photo_url ? '4px solid white' : '4px solid #f0f0f0'};
              margin-bottom: 20px;
            }
            
            .profile-placeholder {
              font-size: 40px;
            }
          }

          .startup-name-container {
            padding: 20px 30px;
            text-align: center;
            background: #ffffff;
            position: relative;
            margin-top: ${card.cover_photo_url ? '-2px' : '0'};
          }

          .startup-contact-section {
            padding: 20px 30px;
            background: #ffffff;
            border-top: 1px solid #f0f0f0;
          }

          .startup-contact {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 24px;
          }

          .contact-item {
            display: flex;
            align-items: center;
            gap: 15px;
            padding: 12px 16px;
            background: #f8f9fa;
            border-radius: 12px;
            transition: all 0.3s ease;
            text-decoration: none;
            color: inherit;
          }

          .contact-item:hover {
            background: #e9ecef;
            transform: translateY(-2px);
          }

          .contact-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }

          .email-icon {
            background: linear-gradient(135deg, #ea4335, #fbbc04);
            color: white;
          }

          .phone-icon {
            background: linear-gradient(135deg, #4285f4, #34a853);
            color: white;
          }

          .website-icon {
            background: linear-gradient(135deg, #4285f4, #9c27b0);
            color: white;
          }

          .location-icon {
            background: linear-gradient(135deg, #ea4335, #ff6b6b);
            color: white;
          }

          .whatsapp-icon {
            background: linear-gradient(135deg, #25d366, #128c7e);
            color: white;
          }

          .contact-info {
            flex: 1;
          }

          .social-section {
            padding-top: 16px;
          }

          .social-title {
            font-size: 16px;
            color: #1a1a1a;
            margin-bottom: 16px;
            text-align: center;
            font-weight: 600;
          }

          .social-links {
            display: flex;
            justify-content: center;
            gap: 12px;
            flex-wrap: wrap;
          }

          .social-link {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }

          .social-link:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          .social-link svg {
            color: white;
            font-size: 20px;
          }

          .content-section {
            margin-top: 20px;
            padding: 20px;
            background: #ffffff;
            border-radius: 18px;
            border: 1px solid #e9ecef;
          }

          .content-title {
            font-size: 16px;
            font-weight: 600;
            color: #1a1a1a;
            margin: 0 0 10px 0;
          }

          .content-text {
            font-size: 13px;
            color: #495057;
            line-height: 1.5;
            margin: 0;
          }

          .gallery-section {
            margin-top: 20px;
            padding: 20px;
            background: #ffffff;
            border-radius: 18px;
            border: 1px solid #e9ecef;
          }

          .gallery-title {
            font-size: 16px;
            font-weight: 600;
            color: #1a1a1a;
            margin: 0 0 15px 0;
            text-align: center;
          }

          .gallery-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
          }

          .gallery-item {
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
            transition: transform 0.3s ease;
          }

          .gallery-item:hover {
            transform: translateY(-3px);
          }

          .gallery-image {
            width: 100%;
            height: 120px;
            object-fit: cover;
          }

          .gallery-caption {
            padding: 10px;
            background: #f8f9fa;
            font-size: 12px;
            color: #495057;
            text-align: center;
          }



          .contact-label {
            font-size: 12px;
            color: #6c757d;
            margin: 0 0 2px 0;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .contact-value {
            font-size: 14px;
            color: #1a1a1a;
            margin: 0;
            font-weight: 500;
          }

          .blue-wave {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 100px;
            background-image: url('../../../assets/cardwave.png');
            background-repeat: no-repeat;
            background-position: bottom center;
            background-size: cover;
            z-index: 2;
            opacity: 1;
            pointer-events: none;
          }

          .blue-wave-img {
            position: absolute;
            left: 0;
            right: 0;
            width: 100%;
            pointer-events: none;
            z-index: 1;
            /* Default styles for web view */
            bottom: -44px;
            height: 195px;
            object-fit: cover;
          }

          /* Mobile styles */
          @media (max-width: 768px) {
            .blue-wave-img {
              bottom: -2px;
              height: 100px;
            }
          }

          .company-logo-corner {
            position: absolute;
            bottom: 15px;
            right: 25px;
            width: 50px;
            height: 50px;
            background: white;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
            z-index: 3;
          }

          .company-logo-corner img {
            width: 35px;
            height: 35px;
            object-fit: contain;
            border-radius: 8px;
          }

          .company-logo-corner .logo-placeholder {
            width: 35px;
            height: 35px;
            background: linear-gradient(135deg, #4285f4, #34a853);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 18px;
            font-weight: bold;
          }

          @media (max-width: 480px) {
            .startup-theme-container {
              max-width: 100%;
              margin: 0;
              border-radius: 0 !important;
            }
            
            .startup-header {
              padding: 30px 20px 45px;
              min-height: 150px;
            }
            
            .startup-name-container {
              padding: 15px 20px;
            }
            
            .about-section {
              padding: 15px 20px;
            }
            
            .about-text {
              font-size: 12px;
              line-height: 1.5;
            }
            
            .achievements-section {
              padding: 15px 20px;
            }
            
            .achievements-title {
              font-size: 14px;
              margin-bottom: 10px;
              text-align: center;
            }
            
            .achievements-grid {
              gap: 10px;
              margin-top: 10px;
            }
            
            .achievement-item {
              padding: 10px;
            }
            
            .achievement-image {
              width: 40px;
              height: 40px;
              margin-bottom: 8px;
            }
            
            .achievement-caption {
              font-size: 10px;
              margin-bottom: 6px;
            }
            
            .achievement-certificate {
              font-size: 8px;
              padding: 3px 6px;
            }
            
            .startup-body {
              padding: 20px;
            }
            
            .startup-footer {
              padding: 15px 20px;
            }
          }

          /* Extra small screens and preview mode */
          @media (max-width: 200px) {
            .startup-theme-container {
              max-width: 100%;
              margin: 0;
              border-radius: 15px;
              font-size: 12px;
            }
            
            .startup-header {
              padding: 15px 10px 10px;
              min-height: 120px;
            }
            
            .startup-name-container {
              padding: 10px;
            }
            
            .profile-image,
            .profile-placeholder {
              width: 70px;
              height: 70px;
              margin-bottom: 12px;
              border-width: 3px;
            }
            
            .profile-placeholder {
              font-size: 28px;
            }
            
            .startup-name {
              font-size: 18px;
            }
            
            .verified-badge {
              font-size: 16px;
            }
            
            .startup-title {
              font-size: 14px;
            }
            
            .startup-company {
              font-size: 12px;
            }
            
            .startup-bio {
              font-size: 11px;
            }
            
            .achievements-section {
              padding: 10px;
            }
            
            .achievements-title {
              font-size: 12px;
              margin-bottom: 8px;
            }
            
            .achievements-grid {
              gap: 8px;
              margin-top: 8px;
            }
            
            .achievement-item {
              padding: 8px;
            }
            
            .achievement-image {
              width: 80px;
              height: 80px;
              object-fit: cover;
              border-radius: 12px;
              border: 2px solid #e9ecef;
              box-shadow: 0 4px 12px rgba(0,0,0,0.08);
              margin-bottom: 12px;
              transition: all 0.3s ease;
            }
            
            .achievement-image:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 16px rgba(0,0,0,0.12);
            }
            
            .achievement-caption {
              font-size: 9px;
              margin-bottom: 4px;
            }
            
            .achievement-certificate {
              font-size: 7px;
              padding: 2px 4px;
            }
            
            .startup-body {
              padding: 15px 10px;
            }
            
            .contact-item {
              padding: 8px;
              gap: 8px;
            }
            
            .contact-icon {
              width: 24px;
              height: 24px;
            }
            
            .contact-label {
              font-size: 9px;
            }
            
            .contact-value {
              font-size: 11px;
            }
            
            .startup-footer {
              padding: 10px;
            }
            
            .footer-text {
              font-size: 9px;
            }
            
            .work-badge {
              font-size: 8px;
              padding: 2px 6px;
              margin-bottom: 10px;
            }
            
            .social-section {
              margin-top: 10px;
              padding: 10px;
            }
            
            .social-title {
              font-size: 12px;
              margin-bottom: 8px;
            }
            
            .social-links {
              gap: 8px;
            }
            
            .social-link {
              width: 30px;
              height: 30px;
            }
            
            .content-section {
              margin-top: 10px;
              padding: 10px;
            }
            
            .content-title {
              font-size: 12px;
              margin-bottom: 5px;
              text-align: center; 
            }
            
            .content-text {
              font-size: 10px;
            }
            
            .gallery-section {
              margin-top: 10px;
              padding: 10px;
            }
            
            .gallery-title {
              font-size: 12px;
              margin-bottom: 8px;
            }
            
            .gallery-grid {
              grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
              gap: 8px;
            }
            
            .gallery-image {
              height: 60px;
            }
            
            .gallery-caption {
              padding: 5px;
              font-size: 8px;
            }
            
            .company-logo-corner {
              bottom: 8px;
              right: 15px;
              width: 30px;
              height: 30px;
            }
            
            .company-logo-corner img,
            .company-logo-corner .logo-placeholder {
              width: 20px;
              height: 20px;
              font-size: 12px;
            }
          }
        `}
      </style>

      <div className="startup-header">
        {/* Cover Photo Background */}
        {coverPhoto && (
          <div 
            className="cover-photo" 
            style={{ backgroundImage: `url(${coverPhoto})` }}
          ></div>
        )}
        
        {/* Overlay for better text readability - Comment out to show full cover photo */}
        <div className="cover-overlay"></div>
        {/* Uncomment below line to remove overlay completely */}
        {/* <div className="cover-overlay" style={{ background: 'transparent' }}></div> */}
        
        {/* Profile Section - Floating over cover */}
        <div className="profile-section">
          {profileImage ? (
            <img 
              src={profileImage} 
              alt={name} 
              className="profile-image"
            />
          ) : (
            <div className="profile-placeholder">
              {name ? name.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
        </div>
        
        {/* Company Logo in Corner */}
        {companyLogo && (
          <div className="company-logo-corner">
            <img src={companyLogo} alt={companyName} />
          </div>
        )}
        {!companyLogo && companyName && (
          <div className="company-logo-corner">
            <div className="logo-placeholder">
              {companyName.charAt(0).toUpperCase()}
            </div>
          </div>
        )}
        
        {/* Blue Wave at Bottom */}
        <img src={require('../../../assets/wave_white.png')} alt="wave" className="blue-wave-img" />
      </div>

      {/* About Section - Below Profile Card */}

      <div className="startup-name-container">
        <h1 className="startup-name">
          {name || 'Your Name'}
          <CheckCircle className="verified-badge" />
        </h1>
        {jobTitle && <p className="startup-title">{jobTitle}</p>}
        {companyName && <p className="startup-company">{companyName}</p>}
      </div>

      {/* Contact Section - Moved here */}
      <div className="startup-contact-section">
        <div className="startup-contact">
          {emailContact && (
            <a href={`mailto:${emailContact.value}`} className="contact-item">
              <div className="contact-icon email-icon">
                <Email />
              </div>
              <div className="contact-info">
                <p className="contact-label">Email</p>
                <p className="contact-value">{emailContact.value}</p>
              </div>
            </a>
          )}

          {phoneContact && (
            <a href={`tel:${phoneContact.value}`} className="contact-item">
              <div className="contact-icon phone-icon">
                <Phone />
              </div>
              <div className="contact-info">
                <p className="contact-label">Phone</p>
                <p className="contact-value">{formatPhoneNumber(phoneContact.value)}</p>
              </div>
            </a>
          )}

          {whatsappContact && (
            <a href={`https://wa.me/${whatsappContact.value.replace(/\D/g, '')}?text=${encodeURIComponent(`Hello ${name || 'there'}, How are you? Shall we talk about your services?`)}`} 
               target="_blank" 
               rel="noopener noreferrer" 
               className="contact-item">
              <div className="contact-icon whatsapp-icon">
                <WhatsApp />
              </div>
              <div className="contact-info">
                <p className="contact-label">WhatsApp</p>
                <p className="contact-value">{formatPhoneNumber(whatsappContact.value)}</p>
              </div>
            </a>
          )}

          {website && (
            <a href={website.startsWith('http') ? website : `https://${website}`} 
               target="_blank" 
               rel="noopener noreferrer" 
               className="contact-item">
              <div className="contact-icon website-icon">
                <Language />
              </div>
              <div className="contact-info">
                <p className="contact-label">Website</p>
                <p className="contact-value">{formatWebsite(website)}</p>
              </div>
            </a>
          )}

          {address && (
            <div className="contact-item">
              <div className="contact-icon location-icon">
                <LocationOn />
              </div>
              <div className="contact-info">
                <p className="contact-label">Location</p>
                <p className="contact-value">{address}</p>
              </div>
            </div>
          )}
        </div>

        {/* Social Media Section - Moved here */}
        {socialLinks.length > 0 && (
          <div className="social-section">
            <h3 className="social-title">Connect with me</h3>
            <div className="social-links">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  style={{ background: getSocialColor(social.platform) }}
                >
                  {getSocialIcon(social.platform)}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* About Section */}
      {bioText && (
        <div className="about-section">
          <p className="about-text">{bioText}</p>
        </div>
      )}

      {/* Achievements Section */}
      {card.achievements && card.achievements.length > 0 && (
        <div className="achievements-section">
          <h3 className="achievements-title">
            🏆 Achievements & Awards
          </h3>
          {console.log('🏆 Achievements data in StartupTheme:', JSON.stringify(card.achievements, null, 2))}
          <div className={`achievements-grid ${
            card.achievements.length === 1 ? 'single' :
            card.achievements.length === 2 ? 'double' :
            card.achievements.length === 3 ? 'triple' : 'multiple'
          }`}>
            {card.achievements.map((achievement, index) => {
              console.log(`🔍 Processing achievement ${index}:`, achievement);
              console.log(`📁 File type for image_url:`, getFileExtension(achievement.image_url));
              
              return (
              <AchievementContainer key={achievement.id || index}>
                {/* 1. Achievement Image */}
                {achievement.image_url && isImageFile(achievement.image_url) && (
                  <AchievementImageWrapper>
                    <AchievementImage 
                      src={getImageUrl(achievement.image_url)} 
                      alt={achievement.caption || `Achievement ${index + 1}`}
                      onLoad={() => {
                        console.log('✅ Image loaded successfully:', getImageUrl(achievement.image_url));
                      }}
                      onError={(e) => {
                        console.error('❌ Image load error for achievement:', achievement);
                        e.target.src = 'https://via.placeholder.com/300x160?text=Image+Not+Found';
                      }}
                    />
                  </AchievementImageWrapper>
                )}
                
                {/* 2. Caption */}
                {achievement.caption && (
                  <AchievementCaption>{achievement.caption}</AchievementCaption>
                )}
                
                {/* 3. View Certificate Link */}
                {achievement.certificate_link && (
                  <AchievementLinks>
                    <AchievementButton 
                      href={achievement.certificate_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate"
                    >
                      <EmojiEvents className="achievement-icon" />
                      View Certificate
                    </AchievementButton>
                  </AchievementLinks>
                )}
                
                {/* 4. YouTube Video */}
                {achievement.youtube_url && (
                  <MediaContainer>
                    <VideoContainer>
                      {!playingVideos[achievement.id || index] ? (
                        <div onClick={() => toggleVideo(achievement.id || index)}>
                          <img 
                            src={`https://img.youtube.com/vi/${getYouTubeVideoId(achievement.youtube_url)}/maxresdefault.jpg`}
                            alt={achievement.caption || `Achievement ${index + 1}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.src = `https://img.youtube.com/vi/${getYouTubeVideoId(achievement.youtube_url)}/hqdefault.jpg`;
                              e.target.onerror = () => {
                                e.target.src = 'https://via.placeholder.com/300x180?text=Video+Thumbnail+Not+Found';
                              };
                            }}
                          />
                          <VideoOverlay>
                            <PlayArrow className="play-icon" />
                          </VideoOverlay>
                        </div>
                      ) : (
                        <YouTubeEmbed
                          src={getYouTubeEmbedUrl(achievement.youtube_url)}
                          title={achievement.caption || `Achievement ${index + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          referrerPolicy="strict-origin-when-cross-origin"
                        />
                      )}
                    </VideoContainer>
                  </MediaContainer>
                )}
                
                {/* 5. Uploaded Video (from video_url or image_url if it's a video) */}
                {((achievement.video_url) || (achievement.image_url && isVideoFile(achievement.image_url))) && (
                  <MediaContainer>
                    <UploadedVideoPlayer 
                      src={getImageUrl(achievement.video_url || achievement.image_url)}
                      controls
                      preload="metadata"
                      onError={(e) => {
                        console.error('❌ Video load error:', e);
                      }}
                    >
                      Your browser does not support the video tag.
                    </UploadedVideoPlayer>
                  </MediaContainer>
                )}
                
                {/* 6. PDF with Blue Color */}
                {achievement.pdf_url && (
                  <AchievementLinks>
                    <AchievementButton 
                      href={getImageUrl(achievement.pdf_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pdf"
                    >
                      <PictureAsPdf className="achievement-icon" />
                      View PDF
                    </AchievementButton>
                  </AchievementLinks>
                )}

              </AchievementContainer>
            );
            })}
          </div>
        </div>
      )}

      {/* Additional Content Sections */}
      {additionalContent.map((content, index) => {
        // Skip if this content is already shown in About section
        const isAboutContent = content.fieldName?.toLowerCase() === 'about' || 
                              content.fieldName?.toLowerCase() === 'bio' || 
                              content.fieldName?.toLowerCase() === 'description' ||
                              content.fieldName?.toLowerCase() === 'about me' ||
                              content.field_name?.toLowerCase() === 'about' || 
                              content.field_name?.toLowerCase() === 'bio' || 
                              content.field_name?.toLowerCase() === 'description' ||
                              content.field_name?.toLowerCase() === 'about me';
        
        if (isAboutContent) return null;
        
        const contentTitle = content.fieldName || content.field_name || 'Content';
        const contentText = content.fieldValue || content.field_value || '';
        
        return (
          <div key={index} className="content-section">
            <h3 className="content-title" style={{textAlign: 'center'}}>{contentTitle}</h3>
            <p className="content-text">{stripHtml(contentText)}</p>
          </div>
        );
      })}

      {/* Batches Section */}
      {card.batches && card.batches.length > 0 && (
        <div className="achievements-section">
          <h3 className="achievements-title">
            🎓 Batches
          </h3>
          <BatchesGrid>
            {card.batches.map((batch, index) => (
              <div key={batch.id || index} className="achievement-item">
                {batch.image_url && (
                  <BatchImage
                    src={batch.image_url}
                    alt={batch.caption || 'Achievement'}
                  />
                )}
                {batch.caption && (
                  <p className="achievement-caption">{batch.caption}</p>
                )}
              </div>
            ))}
          </BatchesGrid>
        </div>
      )}

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <div className="gallery-section">
          <h3 className="gallery-title">Gallery</h3>
          <div className="gallery-grid">
            {galleryImages.map((image, index) => (
              <div key={index} className="gallery-item">
                <img 
                  src={image.url} 
                  alt={image.caption || `Gallery image ${index + 1}`}
                  className="gallery-image"
                />
                {image.caption && (
                  <div className="gallery-caption">{image.caption}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating Container with Save Contact and WhatsApp Form */}
      {!isPreview && !isEditMode && (
        <FloatingContainer minimized={isMinimized}>
          <SaveContactButton 
            onClick={handleDownloadVCard} 
            isMinimized={isFormExpanded}
          >
            <Download />
            <span>Save Contact</span>
          </SaveContactButton>

          <WhatsAppContainer isExpanded={isFormExpanded}>
            {!isFormExpanded ? (
              <WhatsAppButton onClick={handleWhatsAppClick}>
                <WhatsApp />
                Send WhatsApp
              </WhatsAppButton>
            ) : (
              <WhatsAppForm onSubmit={handleWhatsAppSubmit} isExpanded={isFormExpanded}>
                <CountrySelect 
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  <option value="+65">🇸🇬 +65</option>
                  <option value="+91">🇮🇳 +91</option>
                </CountrySelect>
                
                <PhoneInput
                  type="tel"
                  placeholder="Phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength={10}
                />
                
                <ButtonGroup>
                  <SendButton type="submit" disabled={!phoneNumber}>
                    <WhatsApp />
                    Send
                  </SendButton>
                  <IconButton 
                    type="button"
                    onClick={handleCloseForm}
                    variant="close"
                    aria-label="Close form"
                  >
                    <Close />
                  </IconButton>
                </ButtonGroup>
              </WhatsAppForm>
            )}
          </WhatsAppContainer>
        </FloatingContainer>
      )}

      {!isPreview && !isEditMode && (
        <MinimizeContainer minimized={isMinimized}>
          <MinimizeButton 
            onClick={handleMaximize}
            minimized={isMinimized}
            aria-label="Maximize"
          >
            <KeyboardArrowUp />
          </MinimizeButton>
        </MinimizeContainer>
      )}

      {error && (
        <div style={{
          position: 'fixed',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#ff4444',
          color: 'white',
          padding: '6px 12px',
          borderRadius: '6px',
          zIndex: 1000,
          fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {/* Footer */}
      <Box
        sx={{
          width: '100%',
          py: 2,
          mt: 'auto',
          mb: { xs: '60px', sm: '100px' }, // Add margin bottom for floating buttons
          borderTop: '1px solid rgba(0,0,0,0.1)',
          textAlign: 'center',
          background: 'rgba(0,0,0,0.02)'
        }}
      >
        <Typography
          variant="body2"
          style={{
            color: 'rgba(0,0,0,0.6)',
            fontSize: '0.75rem'
          }}
        >
          Powered By Powersoft Techno Solution Pte Ltd
        </Typography>
      </Box>
    </div>
  );
};

export default StartupTheme; 